import React, { useMemo } from "react";
import { ArticleIdNbrArticles, Articles } from "../types/article";
import { Media } from "../types/media";
import {
  AggregatedArticle,
  useAggregatedArticles,
  aggregateArticles,
} from "../hooks/useAggregatedArticles";
import { useTasksByMedia } from "../hooks/useTasksByMedia";
import { useCountryId } from "../../../core/auth/useLoggedInUser";
import { useEDSData } from "../hooks/useEDSData";
import { useSalesStopData } from "../hooks/useSalesStopData";
import { SearchBar } from "../Molecules/SearchBar";
import { useTranslation } from "@coworker/locales";
import { SelectArticles } from "../Molecules/SelectArticles";
import styled from "styled-components";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FilterPanelMobile } from "@coworker/apprestructured/src/shared/filters/components/FilterPanelMobile";
import { useMediaArticlesFilters } from "../hooks/useMediaArticlesFilters";
import { AggregatedArticleList } from "./AggregatedArticleList";
import { EmptyMediaPlaceholder } from "../Molecules/EmptyMediaPlaceholder";
import { MediaTypes } from "../constants";
import { LocalCombinationInfoBox } from "../Molecules/InformationBox";
import { isEmptyCombinationArticle } from "../Helpers/articleHelpers";

const ArticleFiltersContainer = styled("div")`
  border-bottom: 1px solid var(--grey200);
`;

const SearchBarContainer = styled("div")`
  padding: 16px;
`;

const SelectButtonContainer = styled("div")`
  padding: 16px;
  text-align: center;
  position: flex;
  top: 56px;
  background-color: white;
  z-index: 1;
`;

interface AggregatedArticlesTabProps {
  articles: Articles | undefined;
  combinationArticles: { [key: string]: Articles };
  media: Media;
  isSelectArticlesMode: boolean;
  getSelectedArticleIds: (
    fetchSelectedArticleIdsFunction: () => ArticleIdNbrArticles[]
  ) => void;
  setSelectArticleMode: () => void;
}
export const AggregatedArticlesTab = ({
  articles,
  combinationArticles,
  media,
  isSelectArticlesMode,
  getSelectedArticleIds,
  setSelectArticleMode,
}: AggregatedArticlesTabProps) => {
  const { t } = useTranslation();
  const allCombinationArticleIds = useMemo(
    () =>
      Object.values(combinationArticles ?? {}).flatMap((articles) =>
        articles.map((article) => article.productArticleId)
      ),
    [combinationArticles]
  );
  const { taskLinks } = useTasksByMedia(media.id);
  const countryId = useCountryId();
  const allArticleIds = useMemo(() => {
    const mainArticleIds =
      articles?.map((article) => article.productArticleId) || [];
    return [...mainArticleIds, ...allCombinationArticleIds]
      .sort((a, b) => a.localeCompare(b))
      .join(",");
  }, [articles, allCombinationArticleIds]);
  const { data: edsData } = useEDSData(countryId, allArticleIds);
  const { data: salesStopData } = useSalesStopData(
    media.storeId,
    allArticleIds
  );
  const uniqueArticles: AggregatedArticle[] =
    useAggregatedArticles(articles ?? []) ?? [];

  const uniqueCombinationArticles = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(combinationArticles ?? {}).map(([key, articles]) => [
          key,
          aggregateArticles(articles),
        ])
      ),
    [combinationArticles]
  );

  const [selectedArticleIdsNbrArticles, setSelectedArticleIdsNbrArticles] =
    React.useState<ArticleIdNbrArticles[]>([]);
  const [articleSearchResult, setArticleSearchResult] =
    React.useState(uniqueArticles);
  const [isSearchedResult, setIsSearchedResult] = React.useState(false);

  const aggregatedListRef = React.useRef<HTMLDivElement>(null);
  const emptyCombinationArticles =
    articles?.filter(isEmptyCombinationArticle) || [];
  const hasEmptyCombinationArticles = emptyCombinationArticles.length > 0;

  React.useEffect(() => {
    if (!isSelectArticlesMode) setSelectedArticleIdsNbrArticles([]);
  }, [isSelectArticlesMode]);

  // New filtering. Should do nothing if not enabled.
  const articlesToUse = isSearchedResult ? articleSearchResult : uniqueArticles;
  const {
    useFiltersFactory,
    filterValues,
    setFilterValues,
    useFilteredArticleData,
  } = useMediaArticlesFilters(articlesToUse);
  const articlesToShow = useFilteredArticleData(articlesToUse);

  getSelectedArticleIds(() => {
    const sortedAndFiltered = articlesToShow;
    return selectedArticleIdsNbrArticles.filter((a) =>
      sortedAndFiltered!.some((b) => a.articleId === b.id)
    );
  });
  // Merge articles and sub-article for searching.
  const searchableArticles = [
    ...Object.values(uniqueCombinationArticles).flatMap((x) => x),
    uniqueArticles,
  ].flat();

  for (const article of searchableArticles) {
    if (article.parentArticleCombinationNumber) {
      article.parentArticle = searchableArticles.find(
        (a) =>
          a.productArticleId === article.parentArticleCombinationNumber &&
          a.groupId === article.groupId
      );
    }
  }

  if (articles?.length === 0) {
    return <EmptyMediaPlaceholder mediaType={media.mediaType as MediaTypes} />;
  }
  return (
    <>
      <ArticleFiltersContainer>
        <FilterPanelMobile
          useFiltersFactory={useFiltersFactory}
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          feature="media-articles"
        />
      </ArticleFiltersContainer>

      <SearchBarContainer>
        <SearchBar
          articles={searchableArticles ?? []}
          onSearchResults={(
            isSearchResult: boolean,
            result: AggregatedArticle[]
          ) => {
            setIsSearchedResult(isSearchResult);
            setArticleSearchResult(result);
          }}
        />
      </SearchBarContainer>
      {hasEmptyCombinationArticles && (
        <LocalCombinationInfoBox
          title={`${t("localCombinationInformationHeadingString", {
            count: emptyCombinationArticles.length,
          })}`}
          description={`${t("localCombinationInformationDescriptionString")}`}
          buttonText={`${t("seeArticlesString")}`}
          onButtonClick={() => {
            if (aggregatedListRef.current) {
              aggregatedListRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
              });
            }
          }}
        />
      )}

      {!!uniqueArticles && uniqueArticles.length > 0 && (
        <SelectButtonContainer>
          <FixaButton
            type={"secondary"}
            text={
              isSelectArticlesMode
                ? t("cancelString")
                : t("selectArticlesString")
            }
            onClick={setSelectArticleMode}
            fluid
          />
        </SelectButtonContainer>
      )}
      <SelectArticles
        selectedArticles={selectedArticleIdsNbrArticles}
        uniqueArticles={uniqueArticles}
        filteredArticles={articlesToShow}
        isSelected={isSelectArticlesMode}
        onSelectAll={(selectAllChecked: boolean) => {
          if (selectAllChecked) {
            setSelectedArticleIdsNbrArticles(
              uniqueArticles?.map((article) => ({
                articleId: article.id,
                nbrArticles: article.nbrArticles,
                productArticleId: article.productArticleId,
              })) ?? []
            );
          } else {
            setSelectedArticleIdsNbrArticles([]);
          }
        }}
      />
      <AggregatedArticleList
        ref={aggregatedListRef}
        articles={articlesToShow}
        combinationArticles={uniqueCombinationArticles}
        media={media}
        isSelectArticlesMode={isSelectArticlesMode}
        taskLinks={taskLinks}
        edsData={edsData ?? []}
        salesStopData={salesStopData ?? []}
        selectedArticleIdsNbrArticles={selectedArticleIdsNbrArticles}
        setSelectedArticleIdsNbrArticles={setSelectedArticleIdsNbrArticles}
      />
    </>
  );
};
